import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Accordion, AccordionTab} from 'primereact/accordion'
import {Button} from 'primereact/button'
import {Dropdown} from 'primereact/dropdown'
import {Message} from 'primereact/message'
import {RadioButton} from 'primereact/radiobutton'
import {Sidebar} from 'primereact/sidebar'
import {Timeline} from 'primereact/timeline'
import {Tooltip} from 'primereact/tooltip'
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import {useDropzone} from 'react-dropzone'
import {useInView} from 'react-intersection-observer'
import * as XLSX from 'xlsx'
import {ReactComponent as AuditLogIcon} from '../../assets/icons/AuditLogIcon.svg'
import {ReactComponent as DuoFileDownload} from '../../assets/icons/DuoFileDownload.svg'
import {ReactComponent as DuoFileUpLoad} from '../../assets/icons/DuoFileUpLoad.svg'
import {ReactComponent as DuoImport} from '../../assets/icons/DuoImport.svg'
import {ReactComponent as SupportIcon} from '../../assets/icons/SupportIcon.svg'
import Logo from '../../assets/icons/TaVIE-Logo.svg'
import CsvImage from '../../assets/images/csv.png'
import {ReactComponent as EmptyTimeline} from '../../assets/images/Empty-TimeLine.svg'
import XlsxImage from '../../assets/images/xlsx.png'
import {HTTP_STATUS_OK, SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {log} from '../../log/Logs'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {isValidDate} from '../../utils/DateTimeUtils'
import useWindowSize from '../../utils/useWindowSize'
import {useAuth} from '../authentication/AuthProvider'
import {getPageGenericLogs} from '../generic-log/GenericLogEndPoint'
import {usePageGenericLogs} from '../generic-log/query-keys/GenericLogKeysManager'
import {useListHospitals} from '../hospital/query-keys/HospitalQueryManager'
import FlexBox from '../layout/FlexBox'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {importPatients} from '../table-utils/importForm/ImportEndPoint'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import {
    fileEvent,
    fileEventTypes,
    getFileEventKey,
    onGetExportColumnsDataListener,
    removeFileEventListener,
} from './broadcast/FileEvent'
import {
    filterEventTypes,
    filterExplainEventTypes,
    onFilterChangeListener,
    onFilterExplainChangeListener,
} from './broadcast/FilterEvent'
import {
    datasetType,
    exportColumnTypes,
    exportRecordTypes,
    exportTypes,
} from './Constant'
import {useDatatableContext} from './DataTableContext'
import {genericLogQueryType} from './generic-log/GenericLogConstansts'
import ColumnHeaderReference from './patient/ColumnHeaderReference'
import {minimalPatientColumns} from './patient/Constants'
import {
    importErrorType,
    patientColumnHeaderReferenceList,
    valueType,
} from './patient/PatientTableConstants'
import ReviewImportDataTable from './ReviewImportDataTable'
import {tableTypes} from './StyledTableUtils'
import './styles.css'

const exportOptions = [
    {
        type: exportTypes.csv,
        icon: 'pi pi-file-excel',
        label: 'CSV',
        exportType: exportTypes.csv,
    },
    {
        type: exportTypes.excel,
        icon: 'pi pi-file-excel',
        label: 'EXCEL',
        exportType: exportTypes.excel,
    },
    {
        type: exportTypes.pdf,
        icon: 'pi pi-file-excel',
        label: 'PDF',
        exportType: exportTypes.pdf,
    },
]

const ExportItem = ({
    type,
    icon,
    label,
    onClick = () => {},
    active = false,
}) => (
    <div
        className={`${type} export-item ${!!active ? 'active' : ''}`}
        onClick={onClick}>
        <div>
            <i className={icon}></i>
        </div>
        <div>
            <p>{label}</p>
        </div>
        <div>
            <i
                className={`pi pi-check-circle ${
                    !!active ? '' : 'transparent'
                }`}></i>
        </div>
    </div>
)

const InitialHospitalFilterRequest = {}

const StyledDataExchange = forwardRef((props, ref) => {
    useSignals()

    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    const {translateCommons, translateDatatable} = TranslateUtils()

    const {currentHospital} = useUserHospital()
    const {userRole} = useAppConfig()
    const {handleRetrieveExportData} = useDatatableContext()
    const {user} = useAuth()
    const width = useWindowSize()

    const visible = useSignal(false)
    const tableType = useSignal(tableTypes.none)
    const hospitalSelectImport = useSignal(null)
    const exportOption = useSignal({
        exportType: null,
        exportRecordType: null,
        exportColumnType: null,
    })

    const handleShowForm = tableTypeValue => {
        tableType.value = tableTypeValue
        visible.value = true
    }

    const handleHideForm = () => {
        visible.value = false

        importData.value = null
        hasFileError.value = false
        columnsTable.value = []
        errorList.value = []
    }

    const {data: hospitalOptions} = useListHospitals(
        user?.id,
        true,
        InitialHospitalFilterRequest,
        !!user?.id && !!visible.value,
    )

    const file = useSignal(null)
    const importData = useSignal(null)
    const hasFileError = useSignal(false)
    const columnsTable = useSignal([])
    const errorList = useSignal([])
    const reviewTableRef = useRef(null)

    const processData = useCallback(
        dataString => {
            let dataHolderList = []
            let errorHolderList = []

            const dataStringLines = dataString.split(/\n/)
            const headers = dataStringLines[0].split(
                /,(?![^"]*"(?:(?:[^"]*"){2})*[^"]*$)/,
            )

            for (let i = 1; i < dataStringLines.length; i++) {
                // Split row index from Array Data String
                const row = dataStringLines[i].split(
                    /,(?![^"]*"(?:(?:[^"]*"){2})*[^"]*$)/,
                )

                //Row's cols equal Header's cols
                if (headers && row.length === headers.length) {
                    for (const column of minimalPatientColumns) {
                        if (!headers.includes(column)) {
                            errorHolderList.push({
                                field: column,
                                errorType:
                                    importErrorType.missingMinimalColumns,
                            })
                        }
                    }

                    const obj = {}
                    for (let j = 0; j < headers.length; j++) {
                        if (headers[j]) {
                            const rowTemp = row[j]

                            const columnHeaderRefItem =
                                patientColumnHeaderReferenceList.find(
                                    item => item?.column === headers[j],
                                )

                            if (!!columnHeaderRefItem) {
                                if (
                                    columnHeaderRefItem.valueType ===
                                    valueType.enum
                                ) {
                                    if (
                                        !columnHeaderRefItem.acceptedValues.includes(
                                            rowTemp,
                                        )
                                    ) {
                                        errorHolderList.push({
                                            field: headers[j],
                                            rowIndex: i - 1, // Exclude header column index
                                            errorType:
                                                importErrorType.notAcceptedValues,
                                        })
                                    }
                                } else if (
                                    columnHeaderRefItem.valueType ===
                                    valueType.date
                                ) {
                                    // Invalid Date Format (dd-mm-yyyy)
                                    if (!isValidDate(rowTemp)) {
                                        errorHolderList.push({
                                            field: headers[j],
                                            rowIndex: i - 1, // Exclude header column index
                                            errorType:
                                                importErrorType.notAcceptedValues,
                                        })
                                    }
                                }
                            } else {
                                if (
                                    !errorHolderList.some(
                                        error => error?.field === headers[j],
                                    )
                                ) {
                                    errorHolderList.push({
                                        field: headers[j],
                                        errorType:
                                            importErrorType.notAcceptedColumn,
                                    })
                                }
                            }

                            obj[headers[j]] = rowTemp
                        }
                    }

                    let objImport = {
                        ...obj,
                        hospitalId:
                            userRole === SystemRole.USER_WITH_ROLE
                                ? currentHospital?.id
                                : null,
                    }

                    // remove the blank rows
                    if (Object.values(objImport).filter(x => x).length > 0) {
                        dataHolderList.push(objImport)
                    }
                } else {
                    showNotification(
                        severityNotification.warn,
                        'Data mismatch or missing columns between the headers and the table. Please ensure all required columns are present and that the data format is correct.',
                    )
                }
            }

            // Map UID for data list
            let dataListWithUID = []

            if (dataHolderList?.length) {
                for (const dataItem of dataHolderList) {
                    let isExistUID = true

                    while (isExistUID) {
                        let randUID = 'xxxx-xxxx-xxx-xxxx'.replace(
                            /[x]/g,
                            char => {
                                let rand = (Math.random() * 16) | 0
                                let value =
                                    char === 'x' ? rand : (rand & 0x3) | 0x8
                                return value.toString(16)
                            },
                        )

                        if (
                            !dataListWithUID?.length ||
                            (dataListWithUID?.length &&
                                !dataListWithUID?.some(
                                    item => item?.uid === randUID,
                                ))
                        ) {
                            dataListWithUID.push({...dataItem, uid: randUID})
                            isExistUID = false
                        }
                    }
                }
            }

            // Set Data
            importData.value = dataListWithUID
            errorList.value = errorHolderList

            columnsTable.value = headers?.map(headerField => ({
                field: headerField,
                initialShown: true,
            }))
        },
        [importData, errorList, columnsTable, userRole, currentHospital?.id],
    )

    const handleFileUpload = files => {
        const uploadedFile = files[0]
        if (!uploadedFile) {
            hasFileError.value = true
            return
        }

        file.value = uploadedFile
        hasFileError.value = false

        const reader = new FileReader()
        if (uploadedFile) {
            reader.onload = evt => {
                /* Parse data */
                const bstr = evt.target.result
                const wb = XLSX.read(bstr, {type: 'binary'})
                /* Get first worksheet */
                const wsname = wb.SheetNames[0]
                const ws = wb.Sheets[wsname]
                /* Convert array of arrays */
                const data = XLSX.utils.sheet_to_csv(ws, {header: 1})
                processData(data)
            }
            reader.readAsBinaryString(uploadedFile)
        }
    }

    const handleOnChangeHeaderColumn = headerField => {
        columnsTable.value = columnsTable.value?.filter(
            header => header?.field !== headerField,
        )
    }

    const handleOnChangeImportData = value => {
        importData.value = value?.importData
        errorList.value = value?.errorList
    }

    const handleOnDeleteRow = uid => {
        if (!uid) {
            return
        }

        if (!importData.value || !importData.value?.length) {
            return
        }
        importData.value = importData.value?.filter(data => data?.uid !== uid)
    }

    const handleViewImportedPatients = importedAt => {
        onFilterChangeListener(tableType.value, filterEventTypes.advanced, {
            importedAt: importedAt,
        })

        onFilterExplainChangeListener(
            tableType.value,
            filterExplainEventTypes.ADVANCED_FILTER_EXPLAIN_CHANGE,
            [{importReferenceNumber: importedAt}],
        )

        handleHideForm()
    }

    const {getRootProps, getInputProps} = useDropzone({
        onDrop: handleFileUpload,
        multiple: true,
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                ['.xlsx'],
            'application/vnd.ms-excel': ['.xls'],
        },
        maxFiles: 1,
    })

    const renderFileUploader = () => {
        return (
            <React.Fragment>
                <div className="image-uploader mb-5" {...getRootProps()}>
                    <input {...getInputProps()} />
                    <FlexBox
                        flex-direction="row"
                        justify-content="center"
                        align-items="center"
                        className="pt-3">
                        <img
                            src={require('../../assets/images/image_uploader/uploader.png')}
                            alt={'Uploader'}
                            style={{
                                height: '50px',
                            }}
                        />
                    </FlexBox>
                    <h5 className="text-center font-bold">
                        {translateCommons('Drag and drop files here')}
                    </h5>
                    <p className="text-center">
                        ({' '}
                        {translateCommons('Supported Formats: CSV, XLS, XLSX')}{' '}
                        )
                    </p>

                    <div className="mt-2 mb-5">
                        <div className="flex justify-content-center align-items-center">
                            <div
                                className="px-3"
                                style={{
                                    background: '#0D9488',
                                    width: '10vw',
                                    marginBottom: -9,
                                    borderRadius: 8,
                                }}>
                                <p
                                    className="text-center mb-0 font-bold"
                                    style={{color: 'white'}}>
                                    {translateCommons('or')}
                                </p>
                            </div>
                        </div>
                        <hr className="p-0 m-0" />
                    </div>

                    <div className="col-12 cursor-pointer">
                        <h6 className="text-center text-darkcyan font-bold">
                            {translateCommons('Click here to upload')}
                        </h6>
                    </div>
                </div>
            </React.Fragment>
        )
    }

    const formatTimestamp = timestamp => {
        if (!timestamp) return ''

        try {
            const date = new Date(timestamp)

            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
            const day = String(date.getDate()).padStart(2, '0')
            const hours = String(date.getHours()).padStart(2, '0')
            const minutes = String(date.getMinutes()).padStart(2, '0')

            return `${year}-${month}-${day} ${hours}:${minutes}`
        } catch (error) {
            return ''
        }
    }

    const formatFileSize = bytes => {
        if (!bytes || bytes === 0) return '0 B'

        const units = ['B', 'KB', 'MB', 'GB', 'TB']

        let size = bytes
        let unitIndex = 0

        while (size >= 1024 && unitIndex < units.length - 1) {
            size /= 1024
            unitIndex++
        }

        return `${size.toFixed(2)} ${units[unitIndex]}`
    }

    const handleImportFile = async () => {
        if (userRole === SystemRole.ADMIN && !hospitalSelectImport.value) {
            showNotification(
                severityNotification.warn,
                'Please select hospital to import patients !!',
            )
            return
        }

        let toPostData = importData.value?.length > 0 ? importData.value : []

        if (toPostData.length > 0) {
            const response = await importPatients(
                user?.id,
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : hospitalSelectImport.value,
                toPostData,
            )
            if (response === HTTP_STATUS_OK) {
                importData.value = []

                handleHideForm()
            }
        } else {
            showNotification(
                severityNotification.warn,
                'Import data cannot be empty !!',
            )
        }
    }

    const handleOnGetExportColumns = () => {
        onGetExportColumnsDataListener(
            tableType.value,
            exportOption.value.exportColumnType,
        )
    }

    const handleOnPrepareExportData = columns => {
        handleRetrieveExportData(exportOption.value, columns)
    }

    const hospitalItemTemplate = option => {
        return (
            <div className="flex align-items-center">
                <img
                    alt={option.name}
                    src={
                        option?.extraData && option.extraData?.icon
                            ? option.extraData?.icon
                            : Logo
                    }
                    className={`mr-2`}
                    style={{width: '18px'}}
                />
                <div>{option.label}</div>
            </div>
        )
    }

    useEffect(() => {
        fileEvent.on(
            `${getFileEventKey(
                tableType.value,
                fileEventTypes.RECEIVE_EXPORT_COLUMNS_DATA,
            )}`,
            handleOnPrepareExportData,
        )

        return () => {
            removeFileEventListener(
                tableType.value,
                fileEventTypes.RECEIVE_EXPORT_COLUMNS_DATA,
            )
        }
    }, [tableType.value])

    //#region Audit Log
    const pageNumberRef = useRef(0)
    const logPageable = {
        pageRows: 10,
        sortField: 'createdAt',
        sortOrder: -1,
    }
    const logFilterRequest = {
        types: ['importLog', 'exportLog'],
    }
    const {data: genericLogs} = usePageGenericLogs(
        user?.id,
        genericLogQueryType.importAndExport,
        0,
        logPageable.pageRows,
        logPageable.sortField,
        logPageable.sortOrder,
        {
            ...logFilterRequest,
            importExportLogType: tableType.value,
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
        },
        !!tableType.value,
    )

    const convertLogDataToTimelineData = logs => {
        if (!isArrayAndNotEmpty(logs)) {
            return []
        }

        return logs.map(logItem => {
            if (logItem.isEnd) {
                return logItem
            }

            return {
                description: (
                    <div className="flex justify-content-between">
                        <span>
                            {logItem.modifiedBy} has{' '}
                            {logItem?.type === 'importLog'
                                ? 'imported'
                                : 'exported'}{' '}
                            {logItem[logItem?.type]?.totalRecords ? (
                                <span className="font-bold text-darkcyan">
                                    {logItem[logItem?.type]?.totalRecords}{' '}
                                    record(s)
                                </span>
                            ) : (
                                'unknown number of record(s)'
                            )}
                        </span>
                    </div>
                ),
                date: logItem?.createdAt,
                icon:
                    logItem?.type === 'importLog'
                        ? 'pi pi-file-import'
                        : 'pi pi-file-export',
                color: logItem?.type === 'importLog' ? 'darkcyan' : '#f9f9f9',
                type: logItem?.type,
                data: logItem[logItem?.type],
            }
        })
    }

    const timelineLogs = useMemo(() => {
        const hasEnd =
            genericLogs?.totalElements === genericLogs?.content?.length

        if (!isArrayAndNotEmpty(genericLogs?.content)) {
            return []
        }

        const content = hasEnd
            ? [
                  ...genericLogs?.content,
                  {
                      isEnd: true,
                      description: 'End',
                      color: 'black',
                      size: 12,
                  },
              ]
            : [...genericLogs?.content]

        return convertLogDataToTimelineData(content)
    }, [genericLogs?.content])

    //#endregion
    const queryClient = useQueryClient()
    const observerRef = useRef()

    // Load more events when the page changes
    const loadMoreEvents = useCallback(async () => {
        if (pageNumberRef.current >= genericLogs?.totalPages) {
            return
        }

        const nextLogPageData = await getPageGenericLogs(
            user?.id,
            pageNumberRef.current + 1,
            logPageable.pageRows,
            logPageable.sortField,
            logPageable.sortOrder,
            {
                ...logFilterRequest,
                importExportLogType: tableType.value,
                hospitalId:
                    userRole === SystemRole.USER_WITH_ROLE
                        ? currentHospital?.id
                        : null,
            },
        )

        if (isArrayAndNotEmpty(nextLogPageData?.content)) {
            const {genericLogKeys} = QueryKeyManager(
                QueryKeyTypes.genericLog,
                user?.id,
            )

            queryClient.setQueryData(
                genericLogKeys.page({
                    type: genericLogQueryType.importAndExport,
                    pageNumber: 0,
                    pageRows: logPageable.pageRows,
                    sortField: logPageable.sortField,
                    sortOrder: logPageable.sortOrder,
                    filterRequest: {
                        ...logFilterRequest,
                        importExportLogType: tableType.value,
                        hospitalId:
                            userRole === SystemRole.USER_WITH_ROLE
                                ? currentHospital?.id
                                : null,
                    },
                }),
                oldData => {
                    return {
                        ...oldData,
                        content: [
                            ...oldData.content,
                            ...nextLogPageData.content,
                        ],
                    }
                },
            )

            pageNumberRef.current++
        }

        isLoadingMore.current = false
    }, [genericLogs?.totalPages, pageNumberRef.current])

    const {
        ref: inViewRef,
        inView,
        entry,
    } = useInView({
        /* Optional options */
        threshold: 0,
    })

    const isLoadingMore = useRef(false)
    useEffect(() => {
        if (!!inView && !isLoadingMore.current) {
            log('Loading more events')
            isLoadingMore.current = true
            loadMoreEvents()
        }
    }, [inView])

    return (
        <>
            <Sidebar
                id="sidebar-advanced-filter"
                className="sidebar-filter"
                visible={visible.value}
                onHide={handleHideForm}
                showCloseIcon={true}
                fullScreen={width <= 600}
                blockScroll
                position="right"
                baseZIndex={122}>
                <div className="py-2 px-1">
                    {/* Header */}
                    <div className="flex align-items-center">
                        <div className="mr-2">
                            <DuoImport style={{fontSize: '26px'}} />
                        </div>
                        <div>
                            <h3 className="font-bold m-0">Import or Export</h3>
                        </div>
                    </div>
                    <div>
                        <p>
                            <span className="text-darkcyan">Import</span> allows
                            bulk processing of patient records, while{' '}
                            <span className="text-darkcyan">export</span>{' '}
                            facilitates the transfer of patient records
                        </p>
                    </div>
                    <hr />
                    {/* Content */}
                    <div
                        className="relative"
                        style={{
                            height:
                                width > 990
                                    ? 'calc(100vh - 5vh)'
                                    : 'max-content',
                            overflowY: width > 990 ? 'hidden' : 'auto',
                        }}>
                        <div id="filterWrapper" className=" w-full h-full grid">
                            {/* Import/Export Region */}
                            <div className="col-12 lg:col-7 h-full enableScroll">
                                <div className="sticky">
                                    {/* Export Region */}
                                    <div className="mb-3">
                                        <div className="flex align-items-center mb-3">
                                            <div className="mr-3">
                                                <DuoFileDownload
                                                    style={{fontSize: '26px'}}
                                                />
                                            </div>
                                            <div>
                                                <h5 className="mb-0 font-bold">
                                                    {translateDatatable(
                                                        'Export',
                                                    )}
                                                </h5>
                                            </div>
                                        </div>
                                        <div className="flex align-items-center justify-content-between">
                                            <p className="mb-0 font-bold">
                                                Select the format you want to
                                                export the records
                                            </p>
                                            <div>
                                                {!!exportOption.value
                                                    ?.exportType && (
                                                    <small className="font-bold uppercase text-darkcyan">
                                                        {
                                                            exportOption.value
                                                                .exportType
                                                        }
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                        <div className="grid px-0 mb-3">
                                            {exportOptions.map(option => (
                                                <div
                                                    key={option.type}
                                                    className="col-4 px-2">
                                                    <ExportItem
                                                        type={option.type}
                                                        icon={option.icon}
                                                        label={option.label}
                                                        onClick={() => {
                                                            exportOption.value =
                                                                {
                                                                    ...exportOption.value,
                                                                    exportType:
                                                                        option.type,
                                                                }
                                                        }}
                                                        active={
                                                            !!exportOption.value
                                                                ?.exportType &&
                                                            exportOption.value
                                                                ?.exportType ===
                                                                option.type
                                                        }
                                                    />
                                                </div>
                                            ))}
                                        </div>

                                        {/* Records */}
                                        <div className="flex align-items-center justify-content-between">
                                            <p className="mb-0 font-bold">
                                                Select the number of records you
                                                want to export
                                            </p>
                                            <div>
                                                {!!exportOption.value
                                                    ?.exportRecordType && (
                                                    <small className="font-bold uppercase text-darkcyan">
                                                        {convertKeyToLabel(
                                                            exportOption.value
                                                                .exportRecordType,
                                                        )}
                                                    </small>
                                                )}
                                            </div>
                                        </div>

                                        <div className="grid px-0 mb-3">
                                            <div className="col-6  px-2">
                                                <div className="flex align-items-center mb-1">
                                                    <RadioButton
                                                        onChange={e =>
                                                            (exportOption.value =
                                                                {
                                                                    ...exportOption.value,
                                                                    exportRecordType:
                                                                        exportRecordTypes.inViewRecords,
                                                                })
                                                        }
                                                        checked={
                                                            !!exportOption.value
                                                                ?.exportRecordType &&
                                                            exportOption.value
                                                                ?.exportRecordType ===
                                                                exportRecordTypes.inViewRecords
                                                        }
                                                    />
                                                    <label className="ml-2 font-medium">
                                                        {convertKeyToLabel(
                                                            exportRecordTypes.inViewRecords,
                                                        )}
                                                    </label>
                                                </div>
                                                <small className="text-gray-700">
                                                    Export only the records that
                                                    are currently in view
                                                </small>
                                            </div>

                                            <div className="col-6 px-2">
                                                <div className="flex align-items-center mb-1">
                                                    <RadioButton
                                                        onChange={e =>
                                                            (exportOption.value =
                                                                {
                                                                    ...exportOption.value,
                                                                    exportRecordType:
                                                                        exportRecordTypes.allRecords,
                                                                })
                                                        }
                                                        checked={
                                                            !!exportOption.value
                                                                ?.exportRecordType &&
                                                            exportOption.value
                                                                ?.exportRecordType ===
                                                                exportRecordTypes.allRecords
                                                        }
                                                    />
                                                    <label className="ml-2 font-medium">
                                                        {convertKeyToLabel(
                                                            exportRecordTypes.allRecords,
                                                        )}
                                                    </label>
                                                </div>
                                                <small className="text-gray-700">
                                                    Export all records in the
                                                    the current table
                                                </small>
                                            </div>
                                        </div>

                                        {/* Columns */}
                                        <div className="flex align-items-center justify-content-between">
                                            <p className="mb-0 font-bold">
                                                Select the header columns you
                                                want to export
                                            </p>
                                            <div>
                                                {!!exportOption.value
                                                    ?.exportColumnType && (
                                                    <small className="font-bold uppercase text-darkcyan">
                                                        {convertKeyToLabel(
                                                            exportOption.value
                                                                .exportColumnType,
                                                        )}
                                                    </small>
                                                )}
                                            </div>
                                        </div>

                                        <div className="grid px-0 mb-3">
                                            <div className="col-6  px-2">
                                                <div className="flex align-items-center mb-1">
                                                    <RadioButton
                                                        onChange={e =>
                                                            (exportOption.value =
                                                                {
                                                                    ...exportOption.value,
                                                                    exportColumnType:
                                                                        exportColumnTypes.inViewColumns,
                                                                })
                                                        }
                                                        checked={
                                                            !!exportOption.value
                                                                ?.exportColumnType &&
                                                            exportOption.value
                                                                ?.exportColumnType ===
                                                                exportColumnTypes.inViewColumns
                                                        }
                                                    />
                                                    <label className="ml-2 font-medium">
                                                        {convertKeyToLabel(
                                                            exportColumnTypes.inViewColumns,
                                                        )}
                                                    </label>
                                                </div>
                                                <small className="text-gray-700">
                                                    Export the header columns
                                                    that are currently in view
                                                </small>
                                            </div>

                                            <div className="col-6 px-2">
                                                <div className="flex align-items-center mb-1">
                                                    <RadioButton
                                                        onChange={e =>
                                                            (exportOption.value =
                                                                {
                                                                    ...exportOption.value,
                                                                    exportColumnType:
                                                                        exportColumnTypes.allColumns,
                                                                })
                                                        }
                                                        checked={
                                                            !!exportOption.value
                                                                ?.exportColumnType &&
                                                            exportOption.value
                                                                ?.exportColumnType ===
                                                                exportColumnTypes.allColumns
                                                        }
                                                    />
                                                    <label className="ml-2 font-medium">
                                                        {convertKeyToLabel(
                                                            exportColumnTypes.allColumns,
                                                        )}
                                                    </label>
                                                </div>
                                                <small className="text-gray-700">
                                                    Export all the displayed
                                                    header columns as well as
                                                    the hidden ones
                                                </small>
                                            </div>
                                        </div>

                                        <Button
                                            label="EXPORT NOW"
                                            className="col-12 h-3rem"
                                            icon="pi pi-send"
                                            disabled={
                                                !exportOption.value
                                                    ?.exportType ||
                                                !exportOption.value
                                                    ?.exportRecordType ||
                                                !exportOption.value
                                                    ?.exportColumnType
                                            }
                                            onClick={handleOnGetExportColumns}
                                        />
                                    </div>
                                    <hr />
                                    {/* Import Region */}
                                    {tableType.value === tableTypes.patient && (
                                        <div className="mb-3">
                                            <div className="flex align-items-center mb-3">
                                                <div className="mr-3">
                                                    <DuoFileUpLoad
                                                        style={{
                                                            fontSize: '24px',
                                                        }}
                                                    />
                                                </div>
                                                <div>
                                                    <h5 className="mb-0 font-bold">
                                                        {translateDatatable(
                                                            'Import',
                                                        )}
                                                    </h5>
                                                </div>
                                            </div>
                                            <p className="font-bold">
                                                Import your data from CSV or
                                                Excel files
                                            </p>
                                            <div className="mb-5">
                                                {renderFileUploader()}

                                                {hasFileError.value && (
                                                    <Message
                                                        className="w-full mb-3"
                                                        severity="error"
                                                        content={
                                                            <p>
                                                                File is not
                                                                supported.
                                                                Please upload a
                                                                CSV or Excel
                                                                file
                                                            </p>
                                                        }
                                                    />
                                                )}

                                                {!!file.value &&
                                                    file.value?.name && (
                                                        <>
                                                            {userRole ===
                                                                SystemRole.ADMIN && (
                                                                <div className="w-full mb-3">
                                                                    {!hospitalSelectImport.value && (
                                                                        <Message
                                                                            className="w-full mb-2"
                                                                            severity="error"
                                                                            icon="pi pi-exclamation-triangle"
                                                                            text="Please choose the hospital you want to import patients for."
                                                                        />
                                                                    )}
                                                                    <p className="font-bold">
                                                                        Select
                                                                        Hospital
                                                                    </p>
                                                                    <Dropdown
                                                                        className="w-full font-bold"
                                                                        options={
                                                                            hospitalOptions
                                                                        }
                                                                        value={
                                                                            hospitalSelectImport.value
                                                                        }
                                                                        optionLabel="label"
                                                                        optionValue="value"
                                                                        placeholder={translateCommons(
                                                                            'Select hospital for import patients',
                                                                        )}
                                                                        itemTemplate={
                                                                            hospitalItemTemplate
                                                                        }
                                                                        onChange={e =>
                                                                            (hospitalSelectImport.value =
                                                                                e.value)
                                                                        }
                                                                        filter
                                                                        filterBy="label"
                                                                        filterPlaceholder={translateCommons(
                                                                            'Search here',
                                                                        )}
                                                                        emptyMessage={translateCommons(
                                                                            'No records found',
                                                                        )}
                                                                        showFilterClear
                                                                        resetFilterOnHide
                                                                        showClear
                                                                        emptyFilterMessage={translateCommons(
                                                                            'No results found',
                                                                        )}
                                                                        style={{
                                                                            '--inputColor':
                                                                                'rgba(68, 184, 166, 0.1)',
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}

                                                            <p className="font-bold">
                                                                Your Dataset
                                                            </p>
                                                            <FileUploadProgress
                                                                fileName={
                                                                    file.value
                                                                        ?.name
                                                                }
                                                                fileSize={formatFileSize(
                                                                    file.value
                                                                        ?.size,
                                                                )}
                                                                fileType={
                                                                    file.value
                                                                        ?.type
                                                                }
                                                                uploadedAt={formatTimestamp(
                                                                    file.value
                                                                        ?.lastModified,
                                                                )}
                                                                hasError={
                                                                    errorList
                                                                        .value
                                                                        ?.length >
                                                                        0 ||
                                                                    !hospitalSelectImport.value
                                                                }
                                                                // Handle action
                                                                handleOnReview={() =>
                                                                    reviewTableRef.current.showForm(
                                                                        {
                                                                            importData:
                                                                                importData.value,
                                                                            errorList:
                                                                                errorList.value,
                                                                            headerList:
                                                                                columnsTable.value,
                                                                        },
                                                                    )
                                                                }
                                                            />
                                                        </>
                                                    )}

                                                <Button
                                                    label="IMPORT NOW"
                                                    className="col-12 mt-3 h-3rem"
                                                    icon="pi pi-send"
                                                    disabled={
                                                        errorList.value
                                                            ?.length > 0 ||
                                                        (!hospitalSelectImport.value &&
                                                            userRole ===
                                                                SystemRole.ADMIN)
                                                    }
                                                    onClick={handleImportFile}
                                                />

                                                {errorList.value &&
                                                    errorList.value?.length >
                                                        0 && (
                                                        <Message
                                                            className="w-full mt-3"
                                                            severity="error"
                                                            icon="pi pi-exclamation-triangle"
                                                            text="The file
                                                                contains columns
                                                                that do not
                                                                match the
                                                                required column
                                                                headers
                                                                reference. If
                                                                the file is not
                                                                reviewed, the
                                                                system will
                                                                automatically
                                                                ignore these
                                                                columns. Please
                                                                check and ensure
                                                                all columns are
                                                                correctly
                                                                labeled."
                                                        />
                                                    )}
                                            </div>

                                            <div>
                                                <h5 className="font-bold">
                                                    <span className="mr-2">
                                                        <i className="pi pi-question-circle"></i>
                                                    </span>
                                                    How to import data?
                                                </h5>
                                            </div>

                                            <div className="mb-3">
                                                <Accordion activeIndex={null}>
                                                    <AccordionTab header="Prepare your dataset">
                                                        <PrepareDataset />
                                                    </AccordionTab>
                                                    <AccordionTab header="Upload your dataset">
                                                        <UploadDataset />
                                                    </AccordionTab>
                                                    <AccordionTab header="Complete Process">
                                                        <CompleteImport />
                                                    </AccordionTab>
                                                </Accordion>
                                            </div>

                                            <div className="flex align-items-center">
                                                <div className="mr-2">
                                                    <SupportIcon
                                                        width={20}
                                                        height={20}
                                                    />
                                                </div>
                                                <div>
                                                    <h5 className="font-bold mb-0">
                                                        Need Help?
                                                    </h5>
                                                </div>
                                            </div>
                                            <p>
                                                <span className="text-darkcyan font-bold">
                                                    Contact our support team
                                                </span>{' '}
                                                for assistance with the import
                                                process
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* Audit Log Region */}
                            <div
                                className="col-12 lg:col-5 h-full enableScroll"
                                style={{
                                    position: 'relative',
                                }}>
                                <div>
                                    <div className="flex align-items-center mb-3">
                                        <div>
                                            <AuditLogIcon className="icon-size mr-2" />
                                        </div>
                                        <div>
                                            <h5 className="mb-0 font-bold">
                                                {translateDatatable(
                                                    'Audit Log',
                                                )}
                                            </h5>
                                        </div>
                                    </div>
                                    {timelineLogs?.length ? (
                                        <div>
                                            <Timeline
                                                value={timelineLogs || []}
                                                marker={item => (
                                                    <span
                                                        className={`flex align-items-center justify-content-center border-circle z-1 shadow-1`}
                                                        style={{
                                                            backgroundColor:
                                                                item.color,
                                                            color:
                                                                item.color ===
                                                                'darkcyan'
                                                                    ? 'white'
                                                                    : 'black',
                                                            height: item?.size
                                                                ? `${item.size}px`
                                                                : '2rem',
                                                            width: item?.size
                                                                ? `${item.size}px`
                                                                : '2rem',
                                                            marginLeft: `${
                                                                item?.size
                                                                    ? '8px'
                                                                    : '0'
                                                            }`,
                                                        }}>
                                                        <i
                                                            className={
                                                                item.icon
                                                            }></i>
                                                    </span>
                                                )}
                                                content={(item, index) => (
                                                    <div
                                                        className="flex justify-content-between align-items-center"
                                                        ref={
                                                            index ===
                                                            timelineLogs?.length -
                                                                1
                                                                ? inViewRef
                                                                : null
                                                        }>
                                                        <div>
                                                            <div>
                                                                <small className="text-color-secondary">
                                                                    {item.date}
                                                                </small>
                                                            </div>
                                                            <div className="font-bold">
                                                                {
                                                                    item.description
                                                                }
                                                            </div>
                                                        </div>

                                                        {item?.type ===
                                                            'importLog' && (
                                                            <Button
                                                                icon="pi pi-eye"
                                                                className="font-bold p-button-outlined"
                                                                onClick={() =>
                                                                    handleViewImportedPatients(
                                                                        item
                                                                            ?.data
                                                                            ?.importedAt,
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                    ) : (
                                        <div className="flex align-items-start p-6">
                                            <EmptyTimeline
                                                className="w-full"
                                                height={150}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Sidebar>
            <ReviewImportDataTable
                ref={reviewTableRef}
                handleOnChangeHeader={handleOnChangeHeaderColumn}
                handleOnChangeImportData={handleOnChangeImportData}
                handleOnDeleteRow={handleOnDeleteRow}
            />
        </>
    )
})

export default StyledDataExchange

const PrepareDataset = () => {
    const columnHeaderReferenceRef = useRef(null)

    const downloadDataset = (fileType, datasetVersion) => {
        const rootFolder = './patient/patient-dataset'
        const link = document.createElement('a')

        if (fileType === exportTypes.csv) {
            if (datasetVersion === datasetType.minimal) {
                link.href = require(`${rootFolder}/Patient_Dataset_Minimal.csv`)
            } else if (datasetVersion === datasetType.advanced) {
                link.href = require(`${rootFolder}/Patient_Dataset_Advanced.csv`)
            }
        } else if (fileType === exportTypes.excel) {
            if (datasetVersion === datasetType.minimal) {
                link.href = require(`${rootFolder}/Patient_Dataset_Minimal.xlsx`)
            } else if (datasetVersion === datasetType.advanced) {
                link.href = require(`${rootFolder}/Patient_Dataset_Advanced.xlsx`)
            }
        }

        if (!!link.href) {
            link.download = 'Patient_Dataset'
            link.click()
        }
    }

    return (
        <div>
            <h6 className="mb-0 font-bold">
                <span className="mr-2">
                    <i className="pi pi-info-circle"></i>
                </span>
                Preparing your dataset
            </h6>
            <ul>
                <li className="mb-3">
                    Ensure your{' '}
                    <span className="font-bold text-darkcyan">
                        CSV file is properly formatted
                    </span>{' '}
                    with clearly labeled columns and{' '}
                    <span className="font-bold text-darkcyan">
                        saved as .csv extension
                    </span>
                    .
                </li>
                <li className="mb-3">
                    For Excel files , organize data into clean tables and{' '}
                    <span className="font-bold text-darkcyan">
                        saved as .xlsx or .xls extension
                    </span>
                </li>
                <li className="mb-3">
                    Check for <span className="font-bold">common issues</span>:
                    <ul>
                        <li>No missing mandatory fields.</li>
                        <li>
                            Correct formats for dates, numbers, and special
                            data.
                        </li>
                    </ul>
                </li>
            </ul>
            <h6 className="font-bold">
                <span className="mr-2">
                    <i className="pi pi-info-circle"></i>
                </span>
                Example of a well-formatted CSV or Excel file
            </h6>
            <p className="font-bold text-darkcyan">
                - Minimum required fields for a dataset:
            </p>
            <div className="grid">
                <div className="col-6">
                    <div
                        className="flex align-items-center cursor-pointer select-none"
                        onClick={() =>
                            downloadDataset(
                                exportTypes.csv,
                                datasetType.minimal,
                            )
                        }>
                        <div className="mr-2">
                            <img
                                src={CsvImage}
                                alt="csv"
                                style={{
                                    height: '40px',
                                    width: '40px',
                                }}
                            />
                        </div>
                        <div>
                            <p className="font-bold  mb-1">
                                CSV Format Example
                            </p>
                            <small>
                                Minimal version with required fields only
                            </small>
                        </div>
                    </div>
                </div>
                <div className="col-6">
                    <div
                        className="flex align-items-center cursor-pointer select-none"
                        onClick={() =>
                            downloadDataset(
                                exportTypes.excel,
                                datasetType.minimal,
                            )
                        }>
                        <div className="mr-2">
                            <img
                                src={XlsxImage}
                                alt="xlsx"
                                style={{
                                    height: '40px',
                                    width: '40px',
                                }}
                            />
                        </div>
                        <div>
                            <p className="font-bold  mb-1">
                                XLSX Format Example
                            </p>
                            <small>
                                Minimal version with required fields only
                            </small>
                        </div>
                    </div>
                </div>
            </div>
            <p className="font-bold text-darkcyan">
                - Advanced fields for a dataset (optional but recommended for
                better insights):
            </p>
            <div className="grid">
                <div className="col-6">
                    <div
                        className="flex align-items-center cursor-pointer select-none"
                        onClick={() =>
                            downloadDataset(
                                exportTypes.csv,
                                datasetType.advanced,
                            )
                        }>
                        <div className="mr-2">
                            <img
                                src={CsvImage}
                                alt="csv"
                                style={{
                                    height: '40px',
                                    width: '40px',
                                }}
                            />
                        </div>
                        <div>
                            <p className="font-bold  mb-1">
                                CSV Format Example
                            </p>
                            <small>
                                Advanced version with additional fields
                            </small>
                        </div>
                    </div>
                </div>
                <div className="col-6">
                    <div
                        className="flex align-items-center cursor-pointer select-none"
                        onClick={() =>
                            downloadDataset(
                                exportTypes.excel,
                                datasetType.advanced,
                            )
                        }>
                        <div className="mr-2">
                            <img
                                src={XlsxImage}
                                alt="xlsx"
                                style={{
                                    height: '40px',
                                    width: '40px',
                                }}
                            />
                        </div>
                        <div>
                            <p className="font-bold mb-1">
                                XLSX Format Example
                            </p>
                            <small>
                                Advanced version with additional fields
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            <h6 className="font-bold">
                <span className="mr-2">
                    <i className="pi pi-align-left"></i>
                </span>
                Column Header Reference
            </h6>

            <p>
                - This contains accepted headers and column values, this refers
                to a structured document or system that defines both the allowed
                headers (the names of the columns) and the expected types of
                values for those columns
            </p>

            <div
                className="view-document cursor-pointer select-none"
                onClick={() => {
                    columnHeaderReferenceRef.current.showForm()
                }}>
                <i className="pi pi-arrow-circle-right mr-2"></i>
                <span>Click here to view the document</span>
            </div>
            <ColumnHeaderReference ref={columnHeaderReferenceRef} />
        </div>
    )
}

const UploadDataset = () => {
    return (
        <div>
            <ol className="pl-5">
                <li className="mb-2">
                    <div className="font-bold mb-1">Upload your file</div>

                    <ul>
                        <li className="mb-3">
                            <span className="text-darkcyan">
                                Select the file
                            </span>
                            <ul>
                                <li>
                                    Click "Browse" or "Choose File" to locate
                                    your CSV or Excel file.
                                </li>
                                <li>
                                    Select the file and confirm the selection.
                                </li>
                            </ul>
                        </li>
                    </ul>
                    <ul>
                        <li className="mb-3">
                            <span className="text-darkcyan">
                                Drag and drop the file
                            </span>
                            <ul>
                                <li>
                                    Drag your CSV or Excel file and drop it in
                                    the designated area.
                                </li>
                                <li>Confirm the file selection and proceed.</li>
                            </ul>
                        </li>
                    </ul>
                </li>
                <li className="mb-2">
                    <div className="font-bold mb-1">
                        Map columns (if required):
                    </div>
                    <ul>
                        <li>
                            Ensure each column in your file is{' '}
                            <span className="text-darkcyan">
                                correctly mapped
                            </span>
                            {` `}
                            to the system’s fields.
                        </li>
                        <li>
                            <span className="text-darkcyan">
                                Fix any mismatched or missing fields
                            </span>{' '}
                            in the original file if prompted.
                        </li>
                    </ul>
                </li>
                <li className="mb-2">
                    <div className="font-bold mb-1">
                        Review the data to ensure everything is{' '}
                        <span className="text-darkcyan">
                            aligned correctly.
                        </span>
                    </div>
                </li>
                <li className="mb-2">
                    <div className="font-bold mb-1">
                        Handle errors if they occur:
                    </div>
                    <ul>
                        <li>
                            <span className="text-darkcyan">
                                Correct mismatched fields or unsupported formats
                            </span>{' '}
                            in the file and re-upload.
                        </li>
                    </ul>
                </li>
            </ol>
        </div>
    )
}

const CompleteImport = () => {
    return (
        <div>
            <ul>
                <li className="mb-3">
                    If errors occur,{' '}
                    <span className="text-darkcyan">
                        review the error message, correct issues
                    </span>{' '}
                    in the file, and try again.
                </li>
                <li className="mb-3">
                    After a successful import,{' '}
                    <span className="text-darkcyan">verify the data</span> in
                    your system to ensure everything is correct.
                </li>
                <li className="mb-3">
                    <span className="text-darkcyan">
                        Handle post-import errors
                    </span>{' '}
                    by checking logs or error reports, making corrections, and
                    re-importing as necessary.
                </li>
            </ul>
        </div>
    )
}

const FileUploadProgress = ({
    fileName,
    fileSize,
    fileType,
    uploadedAt,
    progress,
    hasError,
    handleOnImport = () => {},
    handleOnReview = () => {},
}) => {
    return (
        <div className="file-upload-progress w-full">
            <div className="file-info">
                <div className="flex flex align-items-center">
                    <div className="file-icon">
                        <i
                            className={`fas ${
                                fileType === 'text/csv'
                                    ? 'fa-file-csv'
                                    : 'fa-file-excel'
                            }`}></i>
                    </div>
                    <div className="file-details">
                        <span className="file-name mb-1">{fileName}</span>
                        <span className="file-meta">
                            {fileSize} • {uploadedAt}
                        </span>
                    </div>
                </div>

                {/* <div className="cancel-upload">
                    <button className="cancel-button">✕</button>
                    <i className="pi pi-upload"></i>
                </div> */}
                <div className="flex align-items-center">
                    {/* <div
                        className={`start-importing mr-2 p-3 border-circle ${
                            hasError
                                ? 'opacity-50 select-none'
                                : 'cursor-pointer hover:bg-primary-reverse '
                        }`}
                        onClick={handleOnImport}>
                        <i className="font-bold pi pi-upload"></i>

                        <Tooltip
                            style={{
                                zIndex: '999999',
                            }}
                            target=".start-importing"
                            position="top"
                            content={`${
                                !!hasError
                                    ? 'Resolve the errors in the file by reviewing it before importing to avoid mistakes during the import process'
                                    : 'Start importing'
                            }`}
                        />
                    </div> */}

                    <div
                        className="review-data p-3 cursor-pointer border-circle hover:bg-primary-reverse"
                        onClick={handleOnReview}>
                        <i className="font-bold pi pi-eye"></i>

                        <Tooltip
                            style={{
                                zIndex: '999999',
                            }}
                            target=".review-data"
                            position="top"
                            content="Review the imported data"
                        />
                    </div>
                </div>
            </div>
            {/* <div className="progress-bar">
                <div className="progress" style={{width: `${progress}%`}}></div>
            </div>
            <div className="progress-percentage">{progress}%</div> */}
        </div>
    )
}
